import {
  SSO_AUTO_REDIRECT_COOLDOWN_MS,
  SSO_AUTO_REDIRECT_STORAGE_KEY,
} from "../constants/sso";

// NOTE: NSAI-6319 — the backstop behind the `?error=` test in Sso.tsx, which
// stops the loop the SAML callback creates when it redirects back with a
// rejection. This one stops any other lap that puts the browser on /auth/sso
// again within a second or two, whatever the reason and whether or not the
// callback managed to name one.
//
// It has to live in storage rather than in a ref or in state: the auto-redirect
// is a full page navigation, so every lap of the loop is a fresh mount with
// fresh memory, and nothing in the component survives to be counted.
export const wasSsoAutoRedirectJustAttempted = (): boolean => {
  try {
    const storedAt = sessionStorage.getItem(SSO_AUTO_REDIRECT_STORAGE_KEY);

    if (storedAt === null) {
      return false;
    }

    const attemptedAt = Number(storedAt);

    if (!Number.isFinite(attemptedAt)) {
      return false;
    }

    const elapsed = Date.now() - attemptedAt;

    // NOTE: A negative elapsed time means the stored stamp is in the future —
    // a clock change, not a loop — and it reads as "no recent attempt" so the
    // guard cannot lock a user out of SSO until the clock catches up. The
    // `?error=` check is the primary defence; this one may fail open.
    return elapsed >= 0 && elapsed < SSO_AUTO_REDIRECT_COOLDOWN_MS;
  } catch {
    // NOTE: Reading sessionStorage throws where site data is blocked. A
    // legitimate login must still go through, so an unreadable guard is treated
    // as no attempt at all.
    return false;
  }
};

export const markSsoAutoRedirectAttempted = (): void => {
  try {
    sessionStorage.setItem(SSO_AUTO_REDIRECT_STORAGE_KEY, String(Date.now()));
  } catch {
    // NOTE: Same as above — a guard that cannot be written is not a reason to
    // fail the redirect it guards.
  }
};

// NOTE: Called from the token-exchange landing page, because a completed login
// proves there was no loop. Without it the cooldown outlives its purpose: a
// user who signs out within the window would meet the email form instead of
// their deployment's auto-redirect.
export const clearSsoAutoRedirectAttempt = (): void => {
  try {
    sessionStorage.removeItem(SSO_AUTO_REDIRECT_STORAGE_KEY);
  } catch {
    // NOTE: Nothing to clear if storage is unavailable — the guard could not
    // have been written in the first place.
  }
};
